import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  Add as AddIcon,
  Receipt as TransactionIcon,
  Mic as TranscriptionIcon,
  Bookmark as BookmarkIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  ShoppingCart as OrdersIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { AccountInfoStats, Transaction, OrderDto, UserCreditSettingsDto } from '../types';
import { usersApi, transactionsApi, paymentsApi, creditsApi } from '../services/api';



interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserAccount: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [accountInfo, setAccountInfo] = useState<AccountInfoStats | null>(null);
  const [creditSettings, setCreditSettings] = useState<UserCreditSettingsDto | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [orders, setOrders] = useState<OrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [tokenCost, setTokenCost] = useState<number>(0.10); // Default fallback value
  const [minCredits, setMinCredits] = useState<number>(2); // Stripe GBP minimum (~£0.30)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Orders pagination state
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
  const [ordersTotalCount, setOrdersTotalCount] = useState(0);

  // Helper function to get transaction type from tab value
  const getTransactionType = (tabIndex: number): string | undefined => {
    switch (tabIndex) {
      case 0: return 'ALL';
      case 1: return 'TRANSCRIPTION';
      case 2: return 'BOOKMARK_GENERATION';
      default: return 'ALL';
    }
  };

  // Function to fetch transactions
  const fetchTransactions = async (pageNum: number, pageSize: number, type: string | undefined) => {
    try {
      setLoading(true);
      const apiResponse = await transactionsApi.getTransactions(pageNum, pageSize, type);
      setTransactions(apiResponse.data.data);
      setTotalCount(apiResponse.data.total);
    } catch (err) {
      setError('Failed to load transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch orders
  const fetchOrders = async (pageNum: number, pageSize: number) => {
    try {
      setOrdersLoading(true);
      const apiResponse = await paymentsApi.getOrders(pageNum, pageSize);
      setOrders(apiResponse.data.data || []);
      console.log("orders: " + apiResponse.data.data);
      setOrdersTotalCount(apiResponse.data.total || 0);
    } catch (err) {
      setError('Failed to load orders');
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        setLoading(true);
        
        // Fetch account info
        const accountInfo = await usersApi.getUserAccountInfo();
        setAccountInfo(accountInfo.data);
        
        // Fetch credit settings
        const creditSettingsResponse = await creditsApi.getCreditSettings();
        setCreditSettings(creditSettingsResponse.data);
        
        // Fetch token cost
        const tokenCostResponse = await usersApi.getTokenCost();
        setTokenCost(tokenCostResponse.data.costPerToken);
        setMinCredits(tokenCostResponse.data.minCredits ?? Math.ceil(0.30 / tokenCostResponse.data.costPerToken));
        
        // Fetch initial transactions (first page, all types)
        await fetchTransactions(0, rowsPerPage, getTransactionType(tabValue));
      } catch (err) {
        setError('Failed to load account data');
        console.error('Error fetching account data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAccountData();
  }, []);

  // Check for payment success parameter and refresh data when returning from payment
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    if (paymentSuccess === 'true') {
      setShowSuccessMessage(true);
      // Remove the parameter from URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('payment_success');
      window.history.replaceState({}, '', newUrl.toString());
    }

    const handleFocus = () => {
      // Refresh account data when window regains focus (e.g., returning from Stripe)
      const fetchAccountData = async () => {
        try {
          const accountInfo = await usersApi.getUserAccountInfo();
          setAccountInfo(accountInfo.data);
        } catch (err) {
          console.error('Error refreshing account data:', err);
        }
      };
      fetchAccountData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [searchParams]);

  const handlePurchaseCredits = async () => {
    if (!purchaseAmount || isNaN(Number(purchaseAmount)) || Number(purchaseAmount) <= 0) {
      return;
    }

    const purchaseAmountNum = Number(purchaseAmount);

    if (purchaseAmountNum < minCredits) {
      setError(`Minimum purchase is ${minCredits} credits (£${(minCredits * tokenCost).toFixed(2)} GBP minimum).`);
      return;
    }
    
    // Check purchase limit
    if (creditSettings?.maxCreditPurchaseAmount && purchaseAmountNum > creditSettings.maxCreditPurchaseAmount) {
      setError(`Maximum purchase limit is ${creditSettings.maxCreditPurchaseAmount} credits per transaction.`);
      return;
    }

    // Check balance limit
    const currentBalance = accountInfo?.currentBalance || 0;
    const newBalance = currentBalance + purchaseAmountNum;
    if (creditSettings?.maxCreditLimit && newBalance > creditSettings.maxCreditLimit) {
      setError(`This purchase would exceed your maximum balance limit of ${creditSettings.maxCreditLimit} credits. Your current balance is ${currentBalance} credits. Please reduce the purchase amount.`);
      return;
    }

    try {
      setPurchaseLoading(true);
      setError(null); // Clear any previous errors
      
      // Call the backend to create a Stripe checkout session
      const response = await paymentsApi.createCheckoutSession(purchaseAmountNum);
      
      // Redirect to Stripe's payment page
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received from server');
      }
      
    } catch (err) {
      console.error('Purchase failed:', err);
      setError('Failed to initiate payment. Please try again.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'transcription':
        return <TranscriptionIcon color="primary" />;
      case 'bookmark_generation':
        return <BookmarkIcon color="secondary" />;
      case 'credit_purchase':
        return <PaymentIcon color="success" />;
      default:
        return <TransactionIcon />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'transcription':
        return 'primary';
      case 'bookmark_generation':
        return 'secondary';
      case 'credit_purchase':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'PROCESSING':
        return 'info';
      case 'CANCELLED':
        return 'error';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Pagination handlers
  const handleChangePage = async (event: unknown, newPage: number) => {
    setPage(newPage);
    await fetchTransactions(newPage, rowsPerPage, getTransactionType(tabValue));
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    await fetchTransactions(0, newRowsPerPage, getTransactionType(tabValue));
  };

  // Orders pagination handlers
  const handleOrdersChangePage = async (event: unknown, newPage: number) => {
    setOrdersPage(newPage);
    await fetchOrders(newPage, ordersRowsPerPage);
  };

  const handleOrdersChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setOrdersRowsPerPage(newRowsPerPage);
    setOrdersPage(0);
    await fetchOrders(0, newRowsPerPage);
  };

  // Reset page when tab changes
  const handleTabChangeWithReset = async (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    setOrdersPage(0);
    
    if (newValue === 3) {
      // Orders tab
      await fetchOrders(0, ordersRowsPerPage);
    } else {
      // Transaction tabs
      await fetchTransactions(0, rowsPerPage, getTransactionType(newValue));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Account & Transactions
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Maximum transcription recording length:{' '}
        {creditSettings?.maxTranscriptionDurationMinutes ?? 70} minutes
      </Typography>

      {/* Account Balance Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <BalanceIcon color="primary" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Current Balance
                </Typography>
                <Typography variant="h4">
                  {accountInfo?.currentBalance || 0} credits
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <PaymentIcon color="success" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Average usage per day
                </Typography>
                <Typography variant="h4">
                  {accountInfo?.aveCreditsUsedPerDayLast30Days || 0} credits
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <TranscriptionIcon color="primary" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Transcription Used last 30 days
                </Typography>
                <Typography variant="h4">
                  {accountInfo?.transcriptionCreditsUsedLast30Days || 0} credits
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <BookmarkIcon color="secondary" sx={{ mr: 1 }} />
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Bookmarks Used last 30 days
                </Typography>
                <Typography variant="h4">
                  {accountInfo?.bookmarkCreditsUsedLast30Days || 0} credits
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Purchase Credits Button */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                Purchase Credits
              </Typography>
              <Typography color="textSecondary">
                Need more credits? Purchase additional credits to continue using our services.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPurchaseDialogOpen(true)}
              size="large"
            >
              Purchase Credits
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChangeWithReset}>
              <Tab label="All Transactions" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="Transcription Usage" icon={<TranscriptionIcon />} iconPosition="start" />
              <Tab label="Bookmark Usage" icon={<BookmarkIcon />} iconPosition="start" />
              <Tab label="Orders" icon={<OrdersIcon />} iconPosition="start" />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                      {new Date(transaction.createdAt).toLocaleDateString()} {new Date(transaction.createdAt).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getTransactionIcon(transaction.operationType)}
                          <Chip 
                            label={transaction.operationType.replace('_', ' ')} 
                            color={getTransactionColor(transaction.operationType) as any}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell align="right">
                        <Typography
                          color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {transaction.balanceAfter > transaction.balanceBefore ? '+' : '-'}{transaction.amount} credits
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.status} 
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Credits Used</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                      {new Date(transaction.createdAt).toLocaleDateString()} {new Date(transaction.createdAt).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="bold">
                          {Math.abs(transaction.amount)} credits
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.status} 
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Credits Used</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                      {new Date(transaction.createdAt).toLocaleDateString()} {new Date(transaction.createdAt).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="bold">
                          {Math.abs(transaction.amount)} credits
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.status} 
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order Number</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Credits</TableCell>
                    <TableCell align="right">Price per Credit</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : !orders || orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary">No orders found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {order.orderNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                         {new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString()}
                        </TableCell>
                        <TableCell align="right">
                          {order.creditsAmount} credits
                        </TableCell>
                        <TableCell align="right">
                          £{order.pricePerCredit.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            £{order.totalAmount.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status} 
                            color={getOrderStatusColor(order.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {order.paymentReference || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={ordersTotalCount}
                rowsPerPage={ordersRowsPerPage}
                page={ordersPage}
                onPageChange={handleOrdersChangePage}
                onRowsPerPageChange={handleOrdersChangeRowsPerPage}
              />
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Purchase Credits Dialog */}
      <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)}>
        <DialogTitle>Purchase Credits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter the number of credits you would like to purchase. Credits are used for transcription and bookmark generation services.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Credits"
            type="number"
            fullWidth
            variant="outlined"
            value={purchaseAmount}
            onChange={(e) => {
              const value = e.target.value;
              setPurchaseAmount(value);
              // Clear error when user starts typing
              if (error) setError(null);
            }}
            inputProps={{ 
              min: minCredits,
              max: creditSettings?.maxCreditPurchaseAmount 
            }}
            error={!!error && (error.includes('exceed') || error.includes('Maximum purchase') || error.includes('Minimum purchase'))}
            helperText={
              [
                `Minimum: ${minCredits} credits`,
                creditSettings?.maxCreditPurchaseAmount
                  ? `Maximum: ${creditSettings.maxCreditPurchaseAmount} credits per transaction`
                  : null,
              ].filter(Boolean).join(' · ')
            }
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Estimated cost: £{purchaseAmount ? (Number(purchaseAmount) * tokenCost).toFixed(2) : '0.00'} (£{tokenCost.toFixed(2)} per credit)
          </Typography>
          {creditSettings?.maxCreditLimit && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
              Maximum account balance: {creditSettings.maxCreditLimit} credits (Current: {accountInfo?.currentBalance || 0} credits)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handlePurchaseCredits}
            variant="contained"
            disabled={!purchaseAmount || isNaN(Number(purchaseAmount)) || Number(purchaseAmount) < minCredits || purchaseLoading}
          >
            {purchaseLoading ? <CircularProgress size={20} /> : 'Purchase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        message="Payment successful! Your credits have been added to your account."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* Error Message Snackbar */}
      <Snackbar
        open={!!error && !error.includes('Failed to load')}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default UserAccount; 